import styled, { css } from 'styled-components';
import { cssVar } from 'src/app/styles/helper';
import Stack from '../Layout/Stack/Stack';
import {
  FontSizePropKeys,
  FontFamilyPropKeys,
  FontWeightPropKeys,
  LineHeightPropKeys,
} from 'src/app/styles/typography';

export type InputSize = 'sm' | 'md' | 'lg';

interface InputContainerProps {
  $size: InputSize;
  $disabled: boolean;
  $actionable: boolean;
  $error?: boolean;
}

interface InputWrapperProps {
  $size: InputSize;
  $disabled: boolean;
  $actionable: boolean;
  $hasLeftElement: boolean;
  $hasRightElement: boolean;
  $fontSize: FontSizePropKeys;
  $fontFamily: FontFamilyPropKeys;
  $fontWeight: FontWeightPropKeys;
  $lineHeight: LineHeightPropKeys;
}

export const inputPadding = {
  sm: `${cssVar('spacing-md')} ${cssVar('spacing-lg')}`,
  md: '10px 14px',
  lg: '19px 14px',
};

export const InputContainer = styled(Stack)`
  width: 100%;
`;

export const InputBox = styled.div<InputContainerProps>`
  display: flex;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${({ $error }) => ($error ? cssVar('border-error') : cssVar('border-primary'))};
  border-radius: ${cssVar('radius-md')};
  background-color: ${cssVar('bg-primary')};
  transition: all 0.2s ease-in-out;
  box-shadow: ${cssVar('shadow-xs')};

  ${({ $size }) => css`
    padding: ${inputPadding[$size]};
  `}

  &:hover {
    border-color: ${({ $disabled, $error }) => {
      if ($disabled) return 'inherit';
      if ($error) return cssVar('border-error');
      return cssVar('border-secondary');
    }};
  }

  &:focus-within {
    border-color: ${({ $disabled, $actionable, $error }) => {
      if ($disabled || !$actionable) return 'inherit';
      if ($error) return cssVar('border-error');
      return cssVar('border-brand');
    }};
    box-shadow: ${({ $disabled, $actionable, $error }) => {
      if ($disabled || !$actionable) return 'none';
      if ($error) return `0 0 0 2px ${cssVar('focus-ring-error')}`;
      return `0 0 0 2px ${cssVar('focus-ring')}`;
    }};
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      background-color: ${cssVar('bg-disabled')};
      cursor: not-allowed;
    `}
`;

export const InputLabel = styled.label`
  font-family: ${cssVar('font-family-body')};
  font-size: ${cssVar('font-size-text-sm')};
  font-weight: ${cssVar('font-weight-medium')};
  line-height: ${cssVar('line-height-text-sm')};
  color: ${cssVar('text-secondary')};
  display: block;

  span {
    color: ${cssVar('text-brand-tertiary')};
    margin-left: ${cssVar('spacing-xxs')};
  }
`;

export const InputContent = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

export const InputWrapper = styled.div<InputWrapperProps>`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;

  input,
  select {
    width: 100%;
    background: transparent;
    border: none;
    outline: none;
    color: ${({ $disabled }) => ($disabled ? cssVar('text-disabled') : cssVar('text-primary'))};
    font-size: ${({ $fontSize }) => cssVar($fontSize)};
    font-family: ${({ $fontFamily }) => cssVar($fontFamily)};
    font-weight: ${({ $fontWeight }) => cssVar($fontWeight)};
    line-height: ${({ $lineHeight }) => cssVar($lineHeight)};
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;

    &::placeholder {
      color: ${cssVar('text-placeholder')};
    }
  }
`;

export const ChevronContainer = styled.div`
  position: absolute;
  top: 50%;
  display: flex;
  height: fit-content;
  right: 0;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 5;
`;
